import { useMemo } from "react";
import { useAuth } from "../lib/auth/useAuth";
import {
  useTasks,
  useLists,
  useCompleteTask,
  useUpdateTask,
} from "../hooks/useTasks";
import { useAssignmentsIndex } from "../hooks/useAssignmentsIndex";
import { useUsers } from "../hooks/useDirectory";
import { useUiStore } from "../state/uiStore";
import { taskToVM, groupTasks } from "../lib/taskVM";
import { useTaskExtras } from "../hooks/useTaskExtras";
import { TaskRow, type TaskRowVM } from "../components/TaskRow";

/** Tâches où je porte un rôle RACI actif (vue « Assignées à moi » du design). */
export function AssignedScreen() {
  const { user } = useAuth();
  const users = useUsers();
  const tasks = useTasks();
  const lists = useLists();
  const completeTask = useCompleteTask();
  const updateTask = useUpdateTask();
  const { selectedTaskId, selectTask } = useUiStore();
  const openIds = useMemo(
    () => (tasks.data ?? []).map((t) => t.id),
    [tasks.data],
  );
  const assignmentsIndex = useAssignmentsIndex(openIds);

  const myRefs = useMemo(() => {
    const p = user?.profile as Record<string, unknown> | undefined;
    const set = new Set(
      [p?.sub, p?.preferred_username, p?.email].filter(
        (x): x is string => typeof x === "string",
      ),
    );
    // Les assignments portent actor_ref = worker_url || username (non couvert par
    // les seuls claims OIDC) → résout l'AdminUser courant et ajoute ses refs réels.
    // ponytail: /admin/users exige ScopeUserAdmin → pour un non-admin users.data
    // est vide et le match retombe sur les claims. Correct dès que TM exposera un
    // endpoint "assigned-to-me" résolu côté serveur (actor_ref = opaque TEXT).
    const me = users.data?.find(
      (u) => set.has(u.username) || set.has(u.email) || set.has(u.id),
    );
    if (me) {
      set.add(me.worker_url);
      set.add(me.id);
      set.add(me.username);
    }
    return set;
  }, [user, users.data]);

  const extras = useTaskExtras(tasks.data);

  const vms = useMemo(() => {
    const out: TaskRowVM[] = [];
    for (const t of tasks.data ?? []) {
      if (t.status === "archived") continue; // asStatus coercerait en "todo"
      const asg = assignmentsIndex.data?.[t.id] ?? [];
      if (asg.some((a) => a.active && myRefs.has(a.actor_ref))) {
        out.push(taskToVM(t, lists.data, extras(t)));
      }
    }
    return out;
  }, [tasks.data, assignmentsIndex.data, lists.data, myRefs, extras]);

  const groups = groupTasks(vms);

  const onToggleDone = (t: TaskRowVM) =>
    t.status === "done"
      ? updateTask.mutate({ id: t.id, body: { status: "todo" } })
      : completeTask.mutate(t.id);

  const renderGroup = (title: string, items: TaskRowVM[], accent?: string) =>
    items.length > 0 && (
      <section key={title}>
        <div
          className="group-title"
          style={accent ? { color: accent } : undefined}
        >
          {title}
          <span style={{ fontWeight: 500 }}>{items.length}</span>
        </div>
        {items.map((t) => (
          <TaskRow
            key={t.id}
            task={t}
            selected={t.id === selectedTaskId}
            onSelect={(id) => selectTask(id === selectedTaskId ? null : id)}
            onToggleDone={onToggleDone}
          />
        ))}
      </section>
    );

  return (
    <>
      <div className="view-header">
        <div className="view-title">Assignées à moi</div>
        <div className="view-caption">
          Tâches où je porte un rôle RACI actif
        </div>
      </div>
      <div className="main-scroll">
        {(tasks.isLoading || assignmentsIndex.isLoading || users.isLoading) && (
          <div className="empty-state">Chargement…</div>
        )}
        {renderGroup("En retard", groups.overdue, "var(--danger)")}
        {renderGroup("Tâches actives", groups.active)}
        {renderGroup("Terminées", groups.done)}
        {!tasks.isLoading &&
          !assignmentsIndex.isLoading &&
          !users.isLoading &&
          vms.length === 0 && (
            <div className="empty-state">Aucune tâche assignée.</div>
          )}
      </div>
    </>
  );
}
